import { motion, useReducedMotion } from 'framer-motion'

/**
 * Premium textual statement block — a calm, on-brand panel used where an
 * animation isn't warranted. Entrance reveal is handled by the parent wrapper;
 * this only adds a soft breathing halo.
 */
export default function ExpertiseStatement({ text }: { text: string }) {
  const reduce = useReducedMotion()
  return (
    <div className="relative overflow-hidden rounded-2xl sm:rounded-3xl border border-ink-100 bg-gradient-to-br from-white via-accent-50/40 to-white p-7 sm:p-10">
      {/* dot pattern */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.3]"
        style={{
          backgroundImage: 'radial-gradient(rgba(59,93,255,0.12) 1px, transparent 1px)',
          backgroundSize: '22px 22px',
          maskImage: 'radial-gradient(ellipse at center, black 30%, transparent 80%)',
        }}
      />
      {/* breathing halo */}
      {reduce ? (
        <div
          aria-hidden
          className="absolute -right-16 -top-16 h-48 w-48 rounded-full blur-3xl"
          style={{ background: 'radial-gradient(circle, rgba(59,93,255,0.14), transparent 70%)' }}
        />
      ) : (
        <motion.div
          aria-hidden
          className="absolute -right-16 -top-16 h-48 w-48 rounded-full blur-3xl"
          style={{ background: 'radial-gradient(circle, rgba(59,93,255,0.16), transparent 70%)' }}
          animate={{ scale: [1, 1.12, 1], opacity: [0.4, 0.65, 0.4] }}
          transition={{ duration: 10, ease: 'easeInOut', repeat: Infinity }}
        />
      )}

      <div className="relative flex items-start gap-4 sm:gap-5">
        <div className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-accent-600 to-accent-700 text-white shadow-[0_14px_30px_-12px_rgba(59,93,255,0.7)]">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M12 3l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z" />
            <path d="M9 12l2 2 4-4" />
          </svg>
        </div>
        <p className="max-w-3xl text-lg sm:text-2xl font-medium leading-snug text-ink-800">{text}</p>
      </div>
    </div>
  )
}
